import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import EquationInput from "../../../../components/inputs/EquationInput";
import FractionInput from "../../../../components/inputs/FractionInput";
import { parseRootFractionInput } from "../../../../components/inputs/RootFractionInput";
import OpenTaskScoringHint from "../../../../components/ui/OpenTaskScoringHint";

const pick = (items) => items[Math.floor(Math.random() * items.length)];
const TOL = 1e-6;

const STORIES = {
  theater: {
    intro: "Na przedstawienie w pewnym teatrze sprzedawano bilety według poniższego cennika:",
    costPrefix: "Po opłaceniu kosztów związanych z organizacją przedstawienia w wysokości",
    costSuffix: "wpływów ze sprzedaży biletów organizatorom pozostało",
    question: "Oblicz liczbę biletów ulgowych sprzedanych na to przedstawienie. Zapisz obliczenia.",
    answer: "biletów ulgowych na przedstawienie",
  },
  cinema: {
    intro: "Na seans w pewnym kinie sprzedawano bilety według poniższego cennika:",
    costPrefix: "Po odliczeniu kosztów organizacji seansu w wysokości",
    costSuffix: "wpływów ze sprzedaży biletów pozostało",
    question: "Oblicz liczbę biletów ulgowych sprzedanych na ten seans. Zapisz obliczenia.",
    answer: "biletów ulgowych na seans",
  },
  aquapark: {
    intro: "W pewnym aquaparku sprzedawano całodniowe bilety wstępu według poniższego cennika:",
    costPrefix: "Po odliczeniu kosztów obsługi aquaparku w wysokości",
    costSuffix: "wpływów ze sprzedaży biletów pozostało",
    question: "Oblicz liczbę sprzedanych ulgowych biletów wstępu. Zapisz obliczenia.",
    answer: "ulgowych biletów wstępu",
  },
  exhibition: {
    intro: "Na interaktywną wystawę dla dzieci sprzedawano bilety według poniższego cennika:",
    costPrefix: "Po opłaceniu kosztów przygotowania wystawy w wysokości",
    costSuffix: "wpływów ze sprzedaży biletów organizatorom pozostało",
    question: "Oblicz liczbę biletów ulgowych sprzedanych na tę wystawę. Zapisz obliczenia.",
    answer: "biletów ulgowych na wystawę",
  },
};

const VARIANTS = [
  { storyKey: "theater", values: [200, 38, 26, 25, 80] },
  { storyKey: "theater", values: [240, 45, 30, 25, 88] },
  { storyKey: "theater", values: [190, 44, 30, 25, 76] },
  { storyKey: "cinema", values: [160, 32, 22, 25, 64] },
  { storyKey: "cinema", values: [180, 40, 28, 20, 75] },
  { storyKey: "cinema", values: [150, 34, 22, 20, 60] },
  { storyKey: "aquapark", values: [120, 90, 65, 20, 45] },
  { storyKey: "aquapark", values: [150, 80, 55, 25, 60] },
  { storyKey: "aquapark", values: [180, 100, 70, 20, 75] },
  { storyKey: "exhibition", values: [240, 18, 12, 25, 96] },
  { storyKey: "exhibition", values: [200, 15, 10, 20, 80] },
  { storyKey: "exhibition", values: [300, 20, 14, 25, 120] },
];

function raw(value) {
  if (typeof value === "object" && typeof value?.raw === "string") return value.raw;
  return String(value ?? "");
}

function number(value) {
  return parseRootFractionInput(raw(value).replace(",", "."));
}

function parseLinearExpression(expression) {
  let value = String(expression ?? "")
    .trim()
    .replace(/,/g, ".")
    .replace(/\s+/g, "")
    .replace(/·/g, "*");

  if (!value) return null;

  const scaledExpression = value.match(/^([+-]?\d+(?:\.\d+)?)\*?\((.+)\)$/);
  if (scaledExpression) {
    const scale = Number(scaledExpression[1]);
    const inner = parseLinearExpression(scaledExpression[2]);
    if (!inner || !Number.isFinite(scale)) return null;
    return {
      ax: scale * inner.ax,
      ay: scale * inner.ay,
      c: scale * inner.c,
    };
  }

  value = value.replace(/\*/g, "").replace(/-/g, "+-");
  if (value.startsWith("+")) value = value.slice(1);

  const result = { ax: 0, ay: 0, c: 0 };
  const terms = value.split("+").filter(Boolean);
  if (!terms.length) return null;

  for (const term of terms) {
    if (term.includes("x") && term.includes("y")) return null;

    if (term.includes("x") || term.includes("y")) {
      const variable = term.includes("x") ? "x" : "y";
      const coefficientText = term.replace(variable, "");
      const coefficient = coefficientText === "" || coefficientText === "+"
        ? 1
        : coefficientText === "-"
          ? -1
          : Number(coefficientText);
      if (!Number.isFinite(coefficient)) return null;
      result[variable === "x" ? "ax" : "ay"] += coefficient;
      continue;
    }

    const constant = Number(term);
    if (!Number.isFinite(constant)) return null;
    result.c += constant;
  }

  return result;
}

function parseLinearEquation(equation) {
  const sides = String(equation ?? "").split("=");
  if (sides.length !== 2) return null;

  const left = parseLinearExpression(sides[0]);
  const right = parseLinearExpression(sides[1]);
  if (!left || !right) return null;

  return {
    ax: left.ax - right.ax,
    ay: left.ay - right.ay,
    c: left.c - right.c,
  };
}

function isBareSolvedEquation(value) {
  const normalized = String(value ?? "").replace(/\s+/g, "").replace(/,/g, ".");
  const numberPattern = "[+-]?(?:\\d+(?:\\.\\d+)?|\\d+\\/\\d+)";
  return new RegExp(`^(?:[xy]=${numberPattern}|${numberPattern}=[xy])$`).test(normalized);
}

function equationsEquivalent(first, second) {
  if (!first || !second) return false;
  const firstValues = [first.ax, first.ay, first.c];
  const secondValues = [second.ax, second.ay, second.c];
  const baseIndex = secondValues.findIndex((value) => Math.abs(value) > TOL);
  if (baseIndex < 0 || Math.abs(firstValues[baseIndex]) <= TOL) return false;

  const scale = firstValues[baseIndex] / secondValues[baseIndex];
  return secondValues.every(
    (value, index) => Math.abs(firstValues[index] - scale * value) <= TOL
  );
}

function equationPreview(equation) {
  return String(equation ?? "").replace(/[·*]/g, "\\cdot ");
}

function buildTask(item, source) {
  const storyKey = item.storyKey || "theater";
  const [totalTickets, normalPrice, reducedPrice, costPercent, reducedTickets] = item.values;
  const normalTickets = totalTickets - reducedTickets;
  const grossRevenue = normalPrice * normalTickets + reducedPrice * reducedTickets;
  const remainingRevenue = grossRevenue * (100 - costPercent) / 100;
  return {
    type: "MaturaTicketRevenueSystemOpenTask",
    source,
    tags: ["matura", "uklad rownan", "procenty", "zadanie tekstowe"],
    data: {
      storyKey, totalTickets, normalPrice, reducedPrice, costPercent,
      reducedTickets, normalTickets, grossRevenue, remainingRevenue,
      maxPoints: 2,
    },
  };
}

export function generateTask(options = {}) {
  if (options.variant === "original" && options.session === "2026_MAJ") {
    return buildTask(
      { storyKey: "theater", values: [200, 35, 25, 25, 78] },
      { variant: "original", exam: "MATURA", session: "2026_MAJ", taskNumber: "11" }
    );
  }
  return buildTask(pick(VARIANTS), { variant: "random", basedOn: "MATURA_2026_MAJ_11", taskNumber: "11" });
}

export function renderTask({ data }) {
  const story = STORIES[data.storyKey] || STORIES.theater;
  const cellStyle = { border: "1px solid currentColor", padding: "7px 14px" };
  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>{story.intro}</p>
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "min(100%, 420px)", margin: "12px auto" }}>
          <thead>
            <tr><th colSpan={2} style={{ ...cellStyle, textAlign: "center" }}>Cennik biletów</th></tr>
            <tr>
              <th style={{ ...cellStyle, textAlign: "left" }}>Rodzaj biletu</th>
              <th style={{ ...cellStyle, textAlign: "center" }}>Cena w złotych</th>
            </tr>
          </thead>
          <tbody>
            <tr><td style={cellStyle}>Normalny</td><td style={{ ...cellStyle, textAlign: "center" }}><InlineMath math={String(data.normalPrice)} /></td></tr>
            <tr><td style={cellStyle}>Ulgowy</td><td style={{ ...cellStyle, textAlign: "center" }}><InlineMath math={String(data.reducedPrice)} /></td></tr>
          </tbody>
        </table>
      </div>
      <p>Łącznie sprzedano <InlineMath math={String(data.totalTickets)} /> biletów.</p>
      <p>
        {story.costPrefix} <InlineMath math={`${data.costPercent}\\%`} />{" "}
        {story.costSuffix} <InlineMath math={`${data.remainingRevenue}\\text{ zł}`} />.
      </p>
      <p>{story.question}</p>
    </div>
  );
}

export function renderInput({ data }, answer = {}, onChange, disabled) {
  return (
    <div style={{ display: "grid", gap: 14 }}>
      <OpenTaskScoringHint variant="methodOptional" />

      <div>
        <div><strong>Krok 1.</strong> Zapisz układ równań lub równoważne równanie z jedną niewiadomą.</div>
        <p style={{ margin: "4px 0 10px" }}>
          Przyjmij, że <InlineMath math="x" /> oznacza liczbę biletów normalnych,
          a <InlineMath math="y" /> liczbę biletów ulgowych.
        </p>
        <div style={{ display: "grid", gap: 10 }}>
          <div>
            <div>Pierwsze równanie:</div>
            <EquationInput
              value={answer.eq1 || ""}
              onChange={(eq1) => onChange?.({ ...answer, eq1 })}
              disabled={disabled}
              placeholder={`np. x + y = ${data.totalTickets}`}
            />
          </div>
          <div>
            <div>Drugie równanie (opcjonalne przy metodzie z jednym równaniem):</div>
            <EquationInput
              value={answer.eq2 || ""}
              onChange={(eq2) => onChange?.({ ...answer, eq2 })}
              disabled={disabled}
              placeholder={`np. ${data.normalPrice}x + ${data.reducedPrice}y = ${data.grossRevenue}`}
            />
          </div>
        </div>
      </div>
      <div>
        <div><strong>Krok 2.</strong> Podaj liczbę biletów ulgowych.</div>
        <FractionInput value={answer.reduced || ""} onChange={(reduced) => onChange?.({ ...answer, reduced })} disabled={disabled} label="y" placeholder="np. 78" />
      </div>
    </div>
  );
}

export function checkAnswer({ data }, answer = {}) {
  const reducedCorrect = Math.abs(number(answer.reduced) - data.reducedTickets) <= TOL;
  const firstEquation = parseLinearEquation(answer.eq1);
  const secondEquation = parseLinearEquation(answer.eq2);
  const ticketCountEquation = { ax: 1, ay: 1, c: -data.totalTickets };
  const revenueEquation = {
    ax: data.normalPrice,
    ay: data.reducedPrice,
    c: -data.grossRevenue,
  };
  const reducedOnlyEquation = {
    ax: 0,
    ay: data.reducedPrice - data.normalPrice,
    c: data.normalPrice * data.totalTickets - data.grossRevenue,
  };
  const normalOnlyEquation = {
    ax: data.normalPrice - data.reducedPrice,
    ay: 0,
    c: data.reducedPrice * data.totalTickets - data.grossRevenue,
  };
  const systemCorrect = Boolean(
    firstEquation && secondEquation &&
    ((equationsEquivalent(firstEquation, ticketCountEquation) &&
      equationsEquivalent(secondEquation, revenueEquation)) ||
     (equationsEquivalent(firstEquation, revenueEquation) &&
      equationsEquivalent(secondEquation, ticketCountEquation)))
  );
  const singleEquation = firstEquation || secondEquation;
  const singleEquationRaw = firstEquation ? answer.eq1 : answer.eq2;
  const singleEquationCorrect = Boolean(
    singleEquation && Boolean(firstEquation) !== Boolean(secondEquation) &&
    !isBareSolvedEquation(singleEquationRaw) &&
    (equationsEquivalent(singleEquation, reducedOnlyEquation) ||
      equationsEquivalent(singleEquation, normalOnlyEquation))
  );
  const methodCorrect = systemCorrect || singleEquationCorrect;
  const score = reducedCorrect ? 2 : methodCorrect ? 1 : 0;
  const parts = {
    "równanie / układ równań opisujący zadanie": methodCorrect,
    "liczba biletów ulgowych": reducedCorrect,
  };

  return {
    isCorrect: score === 2,
    score,
    maxScore: 2,
    parts,
  };
}

export function renderAnswer({ data }) {
  const story = STORIES[data.storyKey] || STORIES.theater;
  return <><InlineMath math={String(data.reducedTickets)} /> {story.answer}</>;
}

export function renderUserAnswer(answer = {}) {
  const eq1 = String(answer.eq1 ?? "").trim();
  const eq2 = String(answer.eq2 ?? "").trim();
  const hasSystem = eq1 || eq2;
  return (
    <div style={{ display: "grid", gap: 4 }}>
      {hasSystem && (
        <BlockMath
          math={`\\begin{cases}${equationPreview(eq1)}\\\\${equationPreview(eq2)}\\end{cases}`}
        />
      )}
      <div>Bilety ulgowe: <InlineMath math={raw(answer.reduced) || "?"} /></div>
    </div>
  );
}

export function renderExplanation({ data }) {
  const story = STORIES[data.storyKey] || STORIES.theater;
  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p><strong>Krok 1.</strong> Obliczamy wpływy przed odjęciem kosztów.</p>
      <BlockMath math={`${100 - data.costPercent}\\%\\cdot W=${data.remainingRevenue}`} />
      <BlockMath math={`W=\\frac{${data.remainingRevenue}}{${String((100 - data.costPercent) / 100).replace(".", "{,}")}}=${data.grossRevenue}\\text{ zł}`} />
      <p><strong>Krok 2.</strong> Układamy układ równań.</p>
      <p>Niech <InlineMath math="x" /> oznacza liczbę biletów normalnych, a <InlineMath math="y" /> liczbę biletów ulgowych.</p>
      <BlockMath math={`\\begin{cases}x+y=${data.totalTickets}\\\\${data.normalPrice}x+${data.reducedPrice}y=${data.grossRevenue}\\end{cases}`} />
      <p><strong>Krok 3.</strong> Podstawiamy <InlineMath math={`x=${data.totalTickets}-y`} /> do równania wpływów.</p>
      <BlockMath math={`${data.normalPrice}(${data.totalTickets}-y)+${data.reducedPrice}y=${data.grossRevenue}`} />
      <BlockMath math={`${data.normalPrice * data.totalTickets}-${data.normalPrice}y+${data.reducedPrice}y=${data.grossRevenue}`} />
      <BlockMath math={`${data.reducedPrice - data.normalPrice}y=${data.grossRevenue - data.normalPrice * data.totalTickets}`} />
      <BlockMath math={`y=${data.reducedTickets}`} />
      <p><strong>Odpowiedź.</strong> Sprzedano <InlineMath math={String(data.reducedTickets)} /> {story.answer}.</p>
    </div>
  );
}
